import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightSmall, ChatFill, LikeFill, Plus } from '@peacecover/icon';
import { useLikeAdaptedFictionEdition } from '../../../domains/scene/ui/hooks/useLikeAdaptedFictionEdition';
import { useManageCreateScene } from '../hooks/useManageCreateScene';
import LoadingSceneContentsOverlay from './LoadingSceneContentsOverlay';
import PreviewHeaderItem from './PreviewHeaderItem';
import ThreadContentItem from './ThreadContentItem';
import { ReactComponent as ThreadIcon } from '../../../icons/thread.svg';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys';
import type { components } from '../../../types/schema';

type AdaptedFictionPreview = components['schemas']['ApiV1AdaptedFictionPreview'];
type ChatIntroContentType = components['schemas']['ApiV1AdaptedFictionChatContent'];
type ThreadIntroContentType = components['schemas']['ApiV1AdaptedFictionThreadContent'];

interface SwipePreviewCardProps {
  preview: AdaptedFictionPreview;
  onOpenCommentBottomSheet: () => void;
}

const SwipePreviewCard = ({ preview, onOpenCommentBottomSheet }: SwipePreviewCardProps) => (
  <motion.div
    className="relative w-full h-full"
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -40 }}
    transition={{ type: 'spring', stiffness: 260, damping: 30 }}
  >
    <PreviewCard preview={preview} onOpenCommentBottomSheet={onOpenCommentBottomSheet} />
  </motion.div>
);

export default SwipePreviewCard;

const PreviewCard = ({
  preview,
  onOpenCommentBottomSheet
}: {
  preview: AdaptedFictionPreview;
  onOpenCommentBottomSheet: () => void;
}) => {
  const { pendingSceneId, handleCreateScene } = useManageCreateScene(preview);
  const navigate = useNavigate();

  const categoryTags = [...(preview?.keywords ?? []), ...(preview?.worldview ?? [])];
  const introContents = preview?.introContents ?? [];

  const { mutateAsync: likeAdaptedFictionEdition } = useLikeAdaptedFictionEdition();

  const handleLikeAdaptedFictionEdition = async () => {
    if (!preview.adaptedFictionEditionId) return;
    try {
      const requestBody = {
        engagement_contexts: [
          {
            context_type: 'AdaptedFictionPreview' as const,
            context_id: preview.adaptedFictionPreviewId
          },
          {
            context_type: 'CastRelationship' as const,
            context_id: localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_CAST_RELATIONSHIP_ID) ?? ''
          }
        ]
      };
      await likeAdaptedFictionEdition({
        adaptedFictionEditionId: preview.adaptedFictionEditionId,
        body: requestBody
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      {pendingSceneId ? <LoadingSceneContentsOverlay /> : null}
      <div
        className="mt-[16px] overflow-hidden flex-1 min-h-0 pb-[67px]"
        style={{
          clipPath: 'inset(0 0 67px 0)'
        }}
      >
        <PreviewHeaderItem
          tags={categoryTags}
          summary={preview?.summary ?? ''}
          className="mb-[8px]"
        />
        <div className="w-full">
          {preview?.previewType === 'SceneChat'
            ? (introContents as ChatIntroContentType[])?.map((introContent, index) => (
                <ChatIntroContent
                  key={`${introContent?.text}-${index}`}
                  introContent={introContent}
                />
              ))
            : (introContents as ThreadIntroContentType[])?.map((introContent, index) => (
                <ThreadContentItem
                  key={`${introContent?.text}-${index}`}
                  speaker={introContent?.speaker ?? undefined}
                  text={introContent?.text}
                />
              ))}
        </div>
      </div>

      <div className="absolute bottom-[67px] left-0 w-full h-[355px] pointer-events-none flex flex-col justify-end bg-[linear-gradient(180deg,rgba(31,193,88,0.00)_0%,#1FC158_84.79%)]">
        <div className="relative h-full">
          <div className="absolute right-[17px] top-[78px] flex flex-col items-center gap-[16px]">
            <button
              type="button"
              className="relative mb-[6px] pointer-events-auto"
              onClick={() => navigate('/scene/contact-us')}
            >
              <img
                src="/assets/scene/sample_author_image.png"
                alt="author"
                className="w-[46px] h-[46px] rounded-full object-cover"
              />
              <div className="rounded-full bg-pure-white p-[2px] absolute bottom-[-6px] left-1/2 -translate-x-1/2">
                <Plus size={14} className="text-main-light-1" />
              </div>
            </button>
            <button
              type="button"
              className="flex flex-col items-center pointer-events-auto"
              onClick={handleLikeAdaptedFictionEdition}
            >
              <LikeFill
                size={32}
                className="text-pure-white"
                colors={preview?.isLiked ? ['#FF45A9', '#FF6A6A'] : []}
              />
              <p className="text-[11px] leading-[13px] text-pure-white font-semibold">
                {preview?.likesCount}
              </p>
            </button>
            <button
              type="button"
              className="flex flex-col items-center pointer-events-auto"
              onClick={onOpenCommentBottomSheet}
            >
              <ChatFill size={32} className="text-pure-white" />
              <p className="text-[11px] leading-[13px] text-pure-white font-semibold">
                {preview?.commentsCount}
              </p>
            </button>
          </div>
        </div>
        <button
          type="button"
          className="px-[24px] mb-[79px] flex items-center gap-[8px] pointer-events-auto"
          onClick={handleCreateScene}
        >
          <p className="text-[14px] font-bold leading-[17px] text-pure-white max-w-[159px] truncate">
            {preview?.fictionTitle}
          </p>
          <div className="flex items-center gap-[4px]">
            <ThreadIcon width={14} height={14} />
            <p className="text-[12px] font-medium leading-[14px] text-pure-white">
              타래 {preview?.contentsCount}
            </p>
            <ArrowRightSmall size={14} className="text-pure-white" />
          </div>
        </button>
      </div>
    </div>
  );
};

const ChatIntroContent = ({ introContent }: { introContent: ChatIntroContentType }) => {
  return null;
};
